/**
 * Fil Shorts public — lecteur vertical plein écran (TikTok-like).
 *
 * Route : /shorts (deep-link `?s={id}` ouvre directement sur le bon short).
 *
 * Navigation :
 *  - Mobile : swipe vertical sur la zone centrale.
 *  - Desktop : molette, flèches haut/bas, boutons chevrons à droite.
 *  - J/K/espace : pause / play.
 *
 * Comptage vues :
 *  - Débouncé 1 s après l'activation d'un short.
 *  - Dédupliqué côté client (localStorage) + serveur (session_hash).
 *
 * Partage : Web Share API si dispo, sinon copie du lien.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Flag,
  Heart,
  Pause,
  Play,
  Plus,
  Share2,
  VolumeX,
  Volume2,
  X,
} from 'lucide-react';
import api from '../api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import useSeo from '../hooks/useSeo';

const SESSION_KEY = 'c2r-shorts-session-id';
const VIEWED_KEY = 'c2r-shorts-viewed';
const LIKED_KEY = 'c2r-shorts-liked';
// Préférence son/muet mémorisée entre les sessions : si l'utilisateur a activé
// le son une fois, on tente de le rejouer AVEC son dès l'arrivée (compte sur
// la "Media Engagement Index" de Chrome). Si le navigateur refuse l'autoplay
// avec son, l'overlay « Appuyez pour activer le son » restera visible.
const MUTED_PREF_KEY = 'c2r-shorts-muted-pref';

function readMutedPreference() {
  try {
    const v = localStorage.getItem(MUTED_PREF_KEY);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch { /* ignore */ }
  return true; // Par défaut muté (politique navigateur stricte)
}

function writeMutedPreference(muted) {
  try {
    localStorage.setItem(MUTED_PREF_KEY, muted ? '1' : '0');
  } catch { /* ignore */ }
}

function getOrCreateSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `c2r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return `c2r-fallback-${Date.now()}`;
  }
}

function getViewedSet() {
  try {
    return new Set(JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function markViewed(id) {
  try {
    const s = getViewedSet();
    s.add(id);
    localStorage.setItem(VIEWED_KEY, JSON.stringify([...s].slice(-500)));
  } catch { /* ignore */ }
}

function getLikedSet() {
  try {
    return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function persistLikedSet(set) {
  try {
    localStorage.setItem(LIKED_KEY, JSON.stringify([...set].slice(-1000)));
  } catch { /* ignore */ }
}

function formatViews(n) {
  if (!n || n < 1) return '0';
  if (n < 1000) return `${n}`;
  if (n < 10000) return `${(n / 1000).toFixed(1).replace('.0', '')}k`;
  if (n < 1e6) return `${Math.floor(n / 1000)}k`;
  return `${(n / 1e6).toFixed(1).replace('.0', '')}M`;
}

const KIND_LABEL = {
  official: 'Officiel',
  quiz: 'Quiz vidéo',
  editorial: 'Dossier C2R',
  member: 'Communauté',
};

const KIND_COLORS = {
  official: 'bg-blue-600',
  quiz: 'bg-amber-500',
  editorial: 'bg-[hsl(var(--primary))]',
  member: 'bg-emerald-600',
};

const THEME_COLORS = {
  equipement: '#2563eb',
  etm: '#f59e0b',
  plateau: '#cf2031',
  circulation: '#7c3aed',
  trajectoires: '#059669',
  freinage: '#dc2626',
  'angles-morts': '#0891b2',
  interfiles: '#9333ea',
  meteo: '#0369a1',
  mentale: '#be185d',
  'gestes-signaleur': '#ea580c',
  autre: '#6b7280',
};

export default function ShortsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [shorts, setShorts] = useState([]);
  const [index, setIndex] = useState(0);
  // Préférence son mémorisée : si l'utilisateur avait activé le son dans une
  // session précédente, on démarre non-muté + userInteracted=true pour que
  // l'iframe charge directement avec `mute=0`. Chrome autorisera l'autoplay
  // avec son si l'engagement utilisateur est suffisant (sinon fallback silencieux).
  const [muted, setMuted] = useState(() => readMutedPreference());
  const [paused, setPaused] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportNote, setReportNote] = useState('');
  const [viewsMap, setViewsMap] = useState({});
  const [likesMap, setLikesMap] = useState({});
  const [likedSet, setLikedSet] = useState(() => getLikedSet());
  const [heartPulses, setHeartPulses] = useState([]);
  const [loading, setLoading] = useState(true);
  // UI auto-fade (comportement TikTok)
  const [uiVisible, setUiVisible] = useState(true);
  // Flag d'interaction : passe à true dès la 1ʳᵉ interaction OU au mount si
  // l'utilisateur a déjà choisi le son dans une session précédente.
  const [userInteracted, setUserInteracted] = useState(() => !readMutedPreference());
  const videoRefs = useRef({});
  const iframeRefs = useRef({});
  const containerRef = useRef(null);
  const sessionId = useMemo(() => getOrCreateSessionId(), []);
  const viewed = useRef(getViewedSet());
  const viewTimer = useRef(null);
  const uiHideTimer = useRef(null);

  // Montre l'UI pendant un court instant puis la cache (TikTok-like).
  const flashUi = useCallback((autoHideMs = 2500) => {
    setUiVisible(true);
    clearTimeout(uiHideTimer.current);
    if (autoHideMs > 0) {
      uiHideTimer.current = setTimeout(() => setUiVisible(false), autoHideMs);
    }
  }, []);

  useSeo({
    title: 'Shorts moto — Code 2 Roues',
    description:
      'Mini-vidéos verticales : sécurité, équipement, trajectoires, plateau, circulation. 100 % moto.',
    canonical: 'https://code2roues.fr/shorts',
  });

  // Lock scroll du document pendant qu'on est sur /shorts, évite les sauts
  // d'image sur iOS Safari quand les barres UI apparaissent/disparaissent.
  // On restaure l'overflow au démontage.
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    const prevOverscroll = document.body.style.overscrollBehavior;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      document.body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  // Chargement initial
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [feed, views, likes] = await Promise.all([
          api.shortsFeed(),
          api.shortsViews(),
          api.shortsLikes().catch(() => ({})),
        ]);
        if (cancelled) return;
        setShorts(feed || []);
        setViewsMap(views || {});
        setLikesMap(likes || {});
        // Deep-link ?s=<id>
        const sid = params.get('s');
        if (sid) {
          const idx = (feed || []).findIndex((x) => x.id === sid);
          if (idx >= 0) setIndex(idx);
        }
      } catch (e) {
        toast.error('Impossible de charger les shorts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = shorts[index];

  // Compte une vue 1 s après l'activation du short
  useEffect(() => {
    if (!current) return;
    clearTimeout(viewTimer.current);
    viewTimer.current = setTimeout(async () => {
      if (viewed.current.has(current.id)) return;
      try {
        await api.shortsRecordView(current.id, sessionId);
        markViewed(current.id);
        viewed.current.add(current.id);
        setViewsMap((m) => ({ ...m, [current.id]: (m[current.id] || 0) + 1 }));
      } catch { /* non bloquant */ }
    }, 1000);
    return () => clearTimeout(viewTimer.current);
  }, [current, sessionId]);

  // Lecture / pause automatique selon l'index
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([id, el]) => {
      if (!el) return;
      if (id === current?.id && !paused) {
        el.muted = muted;
        el.play().catch(() => {
          // Autoplay refusé — probablement parce qu'on tente avec son sans
          // interaction récente dans la session. Fallback silencieux : on
          // bascule en muté et on retente la lecture. L'overlay "Appuyez
          // pour activer le son" s'affichera alors naturellement.
          if (!el.muted) {
            el.muted = true;
            setMuted(true);
            setUserInteracted(false);
            el.play().catch(() => { /* vraie erreur de lecture */ });
          }
        });
      } else {
        el.pause();
        try { el.currentTime = 0; } catch { /* ignore */ }
      }
    });
    // Pilote aussi les iframes YouTube via postMessage API (enablejsapi=1).
    // Cmds supportées : playVideo, pauseVideo, mute, unMute, setVolume.
    Object.entries(iframeRefs.current).forEach(([id, el]) => {
      if (!el || !el.contentWindow) return;
      const send = (func, args = []) => {
        try {
          el.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
        } catch { /* ignore cross-origin si pas prêt */ }
      };
      if (id === current?.id) {
        if (paused) send('pauseVideo');
        else {
          send('playVideo');
          if (muted) send('mute');
          else { send('unMute'); send('setVolume', [100]); }
        }
      } else {
        send('pauseVideo');
      }
    });
  }, [index, paused, muted, current]);

  // Au changement de short : montre brièvement l'UI puis l'estompe.
  useEffect(() => {
    flashUi(2500);
    return () => clearTimeout(uiHideTimer.current);
  }, [index, flashUi]);

  // Dès la 1ʳᵉ interaction utilisateur, on peut activer le son sans violer
  // la politique autoplay. Si l'utilisateur avait déjà basculé muted manuellement
  // via le bouton (muted=false), on respecte sa décision.
  useEffect(() => {
    if (userInteracted && muted) {
      setMuted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInteracted]);

  // Persistance de la préférence son/muet entre les sessions.
  // Dès que l'utilisateur bascule le son, on écrit en localStorage pour que
  // le prochain visitage charge directement dans le bon état.
  useEffect(() => {
    writeMutedPreference(muted);
  }, [muted]);

  // Marque l'interaction au 1er tap/clic/swipe/scroll n'importe où dans la page.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || userInteracted) return undefined;
    const mark = () => setUserInteracted(true);
    el.addEventListener('pointerdown', mark, { once: true });
    el.addEventListener('wheel', mark, { once: true, passive: true });
    el.addEventListener('keydown', mark, { once: true });
    return () => {
      el.removeEventListener('pointerdown', mark);
      el.removeEventListener('wheel', mark);
      el.removeEventListener('keydown', mark);
    };
  }, [userInteracted]);

  // Navigation clavier + molette (desktop)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown') { setIndex((i) => Math.min(shorts.length - 1, i + 1)); e.preventDefault(); }
      else if (e.key === 'ArrowUp') { setIndex((i) => Math.max(0, i - 1)); e.preventDefault(); }
      else if (e.key === ' ' || e.key.toLowerCase() === 'k' || e.key.toLowerCase() === 'j') {
        setPaused((p) => !p); e.preventDefault();
      } else if (e.key.toLowerCase() === 'm') {
        setMuted((m) => !m);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shorts.length]);

  // Swipe tactile mobile + molette desktop
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let startY = null;
    let locked = false;
    const onTouchStart = (e) => { startY = e.touches[0].clientY; };
    const onTouchEnd = (e) => {
      if (startY === null) return;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dy) > 60) {
        if (dy < 0) setIndex((i) => Math.min(shorts.length - 1, i + 1));
        else setIndex((i) => Math.max(0, i - 1));
      }
      startY = null;
    };
    const onWheel = (e) => {
      if (locked) return;
      if (Math.abs(e.deltaY) < 20) return;
      locked = true;
      if (e.deltaY > 0) setIndex((i) => Math.min(shorts.length - 1, i + 1));
      else setIndex((i) => Math.max(0, i - 1));
      setTimeout(() => { locked = false; }, 450);
      e.preventDefault();
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('wheel', onWheel);
    };
  }, [shorts.length]);

  // Partage
  const handleShare = useCallback(async () => {
    if (!current) return;
    const url = `${window.location.origin}/shorts?s=${encodeURIComponent(current.id)}`;
    const share = { title: current.title, text: current.description || '', url };
    try {
      if (navigator.share) {
        await navigator.share(share);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Lien copié dans le presse-papier');
      }
    } catch (e) {
      // Partage annulé par l'user — pas d'erreur
    }
  }, [current]);

  const submitReport = useCallback(async () => {
    if (!current || !reportReason) return;
    try {
      await api.shortsReport(current.id, reportReason, reportNote);
      toast.success('Signalement envoyé — notre équipe va examiner la vidéo.');
      setReportOpen(false);
      setReportReason('');
      setReportNote('');
    } catch (e) {
      toast.error(e.message || 'Impossible d\'enregistrer le signalement');
    }
  }, [current, reportReason, reportNote]);

  // ========= LIKE (double-tap + bouton latéral) =========
  // Déclenche l'animation + persistance locale optimiste + appel API.
  const triggerLike = useCallback(async ({ shortId, x = null, y = null, forceLike = false } = {}) => {
    if (!shortId) return;
    const id = shortId;
    const alreadyLiked = likedSet.has(id);
    // Double-tap = toujours LIKE (jamais unlike). Clic bouton = toggle.
    if (forceLike && alreadyLiked) {
      // Animation coeur quand même pour feedback
      if (x !== null && y !== null) {
        const pulseId = `${id}-${Date.now()}`;
        setHeartPulses((h) => [...h, { id: pulseId, shortId: id, x, y }]);
        setTimeout(() => setHeartPulses((h) => h.filter((p) => p.id !== pulseId)), 850);
      }
      return;
    }
    // UI optimiste
    const nextLiked = new Set(likedSet);
    if (alreadyLiked) nextLiked.delete(id); else nextLiked.add(id);
    setLikedSet(nextLiked);
    persistLikedSet(nextLiked);
    setLikesMap((m) => ({ ...m, [id]: Math.max(0, (m[id] || 0) + (alreadyLiked ? -1 : 1)) }));
    // Animation coeur si tap détecté
    if (!alreadyLiked && x !== null && y !== null) {
      const pulseId = `${id}-${Date.now()}`;
      setHeartPulses((h) => [...h, { id: pulseId, shortId: id, x, y }]);
      setTimeout(() => setHeartPulses((h) => h.filter((p) => p.id !== pulseId)), 850);
    }
    // Appel serveur
    try {
      const r = await api.shortsToggleLike(id, sessionId);
      // Réconcilie avec la réponse serveur
      setLikesMap((m) => ({ ...m, [id]: r.likes }));
      if (r.liked) {
        const s = new Set(likedSet);
        s.add(id);
        setLikedSet(s);
        persistLikedSet(s);
      } else {
        const s = new Set(likedSet);
        s.delete(id);
        setLikedSet(s);
        persistLikedSet(s);
      }
    } catch (e) {
      // Rollback silencieux en cas d'erreur réseau
      setLikedSet(likedSet);
      setLikesMap((m) => ({ ...m, [id]: (m[id] || 0) + (alreadyLiked ? 1 : -1) }));
    }
  }, [likedSet, sessionId]);

  // ========= AUTOPLAY-NEXT =========
  // Déclenché quand la vidéo courante se termine (natif HTML5 ou YouTube IFrame).
  const handleEnded = useCallback(() => {
    setIndex((i) => (i < shorts.length - 1 ? i + 1 : i));
  }, [shorts.length]);

  // Écoute postMessage YouTube IFrame API : state 0 = ENDED
  useEffect(() => {
    const onMessage = (event) => {
      // YouTube envoie des strings JSON ; on filtre strict pour éviter les faux positifs.
      if (!event.data || typeof event.data !== 'string') return;
      if (!event.origin.includes('youtube')) return;
      try {
        const data = JSON.parse(event.data);
        if (data?.event === 'onStateChange' && data?.info === 0) {
          handleEnded();
        }
      } catch { /* non-JSON message, ignore */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleEnded]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="animate-pulse text-sm uppercase tracking-widest">Chargement des shorts…</div>
      </div>
    );
  }

  if (!shorts.length) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white gap-4 p-6">
        <p className="text-sm uppercase tracking-widest text-white/60">Pas encore de shorts</p>
        <Link to="/shorts/proposer" className="underline">Soumettre le premier</Link>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 w-full bg-black overflow-hidden select-none touch-none"
      style={{
        // 100svh (small viewport height) évite les sauts d'image sur iOS Safari
        // lorsque la barre d'URL apparaît/disparaît. Fallback 100dvh si non supporté.
        height: '100svh',
        maxHeight: '100dvh',
      }}
      data-testid="shorts-page"
      onPointerDown={() => flashUi(2500)}
    >
      {/* Fil vertical : chaque short occupe 100% de la hauteur, on empile et on
          scrolle via `transform: translateY(-index*100%)`. */}
      <div
        className="absolute inset-0 transition-transform duration-500 ease-[cubic-bezier(0.22,0.9,0.3,1)]"
        style={{ transform: `translateY(-${index * 100}%)`, willChange: 'transform' }}
      >
        {shorts.map((s, i) => (
          <ShortSlide
            key={s.id}
            short={s}
            active={i === index}
            muted={muted}
            paused={paused}
            uiVisible={uiVisible}
            userInteracted={userInteracted}
            views={viewsMap[s.id] || s.views || 0}
            likes={likesMap[s.id] || 0}
            liked={likedSet.has(s.id)}
            heartPulses={heartPulses.filter((p) => p.shortId === s.id)}
            onDoubleTapLike={(x, y) => triggerLike({ shortId: s.id, x, y, forceLike: true })}
            onEnded={handleEnded}
            registerVideo={(el) => { videoRefs.current[s.id] = el; }}
            registerIframe={(el) => { iframeRefs.current[s.id] = el; }}
            onTogglePause={() => { setPaused((p) => !p); flashUi(2500); }}
          />
        ))}
      </div>

      {/* Overlay central "Appuyez pour activer le son" — visible UNIQUEMENT tant
          que la vidéo est muette ET que l'utilisateur n'a pas interagi.
          Au premier tap/clic sur la page, `userInteracted` passe à true et
          déclenche l'activation du son (useEffect plus haut).
          z-[60] pour passer AU-DESSUS de l'iframe YouTube (z-0) ET du header (z-30). */}
      {muted && !userInteracted && !loading && shorts.length > 0 && (
        <button
          type="button"
          onClick={() => { setUserInteracted(true); setMuted(false); flashUi(2500); }}
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-[60] mx-auto w-max max-w-[90%] px-6 py-4 rounded-full bg-black/80 backdrop-blur-md border-2 border-white/40 text-white font-black uppercase tracking-wider text-[13px] sm:text-[14px] flex items-center gap-2 shadow-2xl pointer-events-auto animate-pulse ring-4 ring-red-500/40"
          data-testid="shorts-unmute-overlay"
          aria-label="Activer le son"
        >
          <Volume2 className="w-5 h-5" />
          Appuyez pour activer le son
        </button>
      )}

      {/* Boutons latéraux desktop (chevrons) */}
      <div
        className={`hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 flex-col gap-3 transition-opacity duration-300 ${uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <button
          type="button"
          aria-label="Short précédent"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white hover:bg-white/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="shorts-prev"
        >
          <ChevronUp className="w-5 h-5 mx-auto" />
        </button>
        <button
          type="button"
          aria-label="Short suivant"
          onClick={() => setIndex((i) => Math.min(shorts.length - 1, i + 1))}
          disabled={index === shorts.length - 1}
          className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white hover:bg-white/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="shorts-next"
        >
          <ChevronDown className="w-5 h-5 mx-auto" />
        </button>
      </div>

      {/* Barre d'actions droite (vues, partage, signaler, son) */}
      <div
        className={`absolute right-3 md:right-24 bottom-24 md:bottom-20 z-20 flex flex-col items-center gap-5 transition-opacity duration-300 ${uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="flex flex-col items-center text-white">
          <Eye className="w-6 h-6" />
          <span className="text-xs font-bold mt-1" data-testid="shorts-views-count">
            {formatViews(viewsMap[current?.id] || current?.views || 0)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => triggerLike({ shortId: current?.id })}
          className="flex flex-col items-center transition-colors active:scale-90"
          aria-label={likedSet.has(current?.id) ? "Retirer le j'aime" : "Aimer"}
          data-testid="shorts-like-btn"
        >
          <Heart
            className={`w-6 h-6 transition-all ${likedSet.has(current?.id) ? 'text-red-500 fill-red-500 scale-110' : 'text-white hover:text-red-400'}`}
          />
          <span
            className={`text-xs font-bold mt-1 ${likedSet.has(current?.id) ? 'text-red-400' : 'text-white'}`}
            data-testid="shorts-likes-count"
          >
            {formatViews(likesMap[current?.id] || 0)}
          </span>
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="flex flex-col items-center text-white hover:text-[hsl(var(--primary))] transition-colors"
          aria-label="Partager"
          data-testid="shorts-share"
        >
          <Share2 className="w-6 h-6" />
          <span className="text-[10px] font-bold mt-1 uppercase">Partage</span>
        </button>
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          className="flex flex-col items-center text-white/80 hover:text-red-400 transition-colors"
          aria-label="Signaler"
          data-testid="shorts-report-btn"
        >
          <Flag className="w-5 h-5" />
          <span className="text-[10px] font-bold mt-1 uppercase">Signaler</span>
        </button>
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          className="flex flex-col items-center text-white hover:text-[hsl(var(--primary))] transition-colors"
          aria-label={muted ? 'Activer le son' : 'Couper le son'}
          data-testid="shorts-mute-btn"
        >
          {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
        </button>
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          className="flex flex-col items-center text-white hover:text-[hsl(var(--primary))] transition-colors"
          aria-label={paused ? 'Lecture' : 'Pause'}
          data-testid="shorts-play-btn"
        >
          {paused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
        </button>
      </div>

      {/* Header fixe en haut — compact et discret pour laisser la vidéo respirer.
          Les 2 boutons (Fermer + Proposer) sont réduits à des icônes avec zone
          de tap suffisante (44px) sans texte intrusif. Auto-fade avec le reste de l'UI. */}
      <div
        className={`absolute top-0 left-0 right-0 z-30 flex items-center justify-between gap-3 px-3 py-2 bg-gradient-to-b from-black/50 to-transparent transition-opacity duration-300 ${uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <Link
          to="/"
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-white/90 hover:text-white flex items-center justify-center transition-colors"
          data-testid="shorts-close"
          aria-label="Fermer les shorts"
        >
          <X className="w-5 h-5" />
        </Link>
        <Link
          to="/shorts/proposer"
          className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-[11px] font-bold hover:bg-white/25"
          data-testid="shorts-propose-link"
        >
          <Plus className="w-3.5 h-3.5" />
          Proposer
        </Link>
      </div>

      {/* Bouton SON FLOTTANT PERMANENT — visible en permanence (NE S'ESTOMPE PAS)
          en bas à droite, dessous la barre d'actions. Permet d'activer/couper
          le son à tout moment, même quand l'UI s'estompe. Priorité maximale (z-50). */}
      <button
        type="button"
        onClick={() => {
          setUserInteracted(true);
          setMuted((m) => !m);
          flashUi(2500);
        }}
        className="absolute right-4 md:right-24 bottom-4 md:bottom-6 z-50 w-12 h-12 rounded-full bg-black/70 backdrop-blur-md border-2 border-white/30 text-white flex items-center justify-center shadow-2xl active:scale-90 transition-all hover:bg-black/85"
        aria-label={muted ? 'Activer le son' : 'Couper le son'}
        data-testid="shorts-sound-floating"
      >
        {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
      </button>

      <style>{`
        @keyframes heart-pop {
          0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
          20%  { transform: scale(1.3) rotate(0deg); opacity: 1; }
          60%  { transform: scale(1) rotate(0deg); opacity: 1; }
          100% { transform: scale(1.1) rotate(0deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          svg[style*="heart-pop"] { animation: none !important; opacity: 0 !important; }
        }
      `}</style>


      {/* Dialog de signalement */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="shorts-report-dialog">
          <DialogHeader>
            <DialogTitle>Signaler cette vidéo</DialogTitle>
            <DialogDescription>
              Dites-nous pourquoi vous souhaitez signaler ce short. L'équipe de modération recevra une alerte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { v: 'illegal', l: 'Contenu illégal ou valorisant une infraction' },
              { v: 'dangerous', l: 'Comportement dangereux pour autrui' },
              { v: 'rights', l: 'Atteinte aux droits (image, auteur, mineur)' },
              { v: 'spam', l: 'Spam, publicité, hors-sujet' },
              { v: 'other', l: 'Autre' },
            ].map((o) => (
              <label key={o.v} className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="report-reason"
                  value={o.v}
                  checked={reportReason === o.v}
                  onChange={(e) => setReportReason(e.target.value)}
                  data-testid={`shorts-report-reason-${o.v}`}
                />
                {o.l}
              </label>
            ))}
            <textarea
              className="w-full rounded-md border border-border bg-background p-2 text-sm"
              rows={3}
              placeholder="Précisions (facultatif, 600 car. max)"
              maxLength={600}
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              data-testid="shorts-report-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button onClick={submitReport} disabled={!reportReason} data-testid="shorts-report-submit">
              Envoyer le signalement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Un short individuel (occupe 100% de la vue).
 * Les vidéos statiques utilisent <video>, les YouTube/Vimeo utilisent <iframe>.
 */
function ShortSlide({ short, active, muted, paused, uiVisible, views, likes, liked, heartPulses, onDoubleTapLike, onEnded, registerVideo, registerIframe, onTogglePause, userInteracted }) {
  const isEmbed = short.embed_url || short.youtube_id;
  const isNative = short.source_type === 'static' || short.source_type === 'upload';
  const themeColor = THEME_COLORS[short.theme] || THEME_COLORS.autre;
  const kindLabel = KIND_LABEL[short.kind] || KIND_LABEL.editorial;
  const kindColor = KIND_COLORS[short.kind] || KIND_COLORS.editorial;

  // Double-tap : 2 taps dans 320ms → like + animation cœur à l'emplacement du tap.
  // Un seul tap = toggle UI (géré par le parent via `onPointerDown` du conteneur).
  // Un seul tap long (plus de 300ms sans 2e tap) = pause (via onTogglePause).
  const lastTap = useRef(0);
  const singleTapTimer = useRef(null);
  const handleTap = useCallback((e) => {
    const now = Date.now();
    const target = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX || target.width / 2) - target.left;
    const y = (e.clientY || e.touches?.[0]?.clientY || target.height / 2) - target.top;
    if (now - lastTap.current < 320) {
      // Double tap détecté
      clearTimeout(singleTapTimer.current);
      lastTap.current = 0;
      onDoubleTapLike?.(x, y);
    } else {
      lastTap.current = now;
      clearTimeout(singleTapTimer.current);
      // Déclenche pause/play 320ms plus tard SI pas de 2e tap entre temps
      singleTapTimer.current = setTimeout(() => {
        onTogglePause?.();
      }, 330);
    }
  }, [onDoubleTapLike, onTogglePause]);

  // URL embed YouTube enrichie :
  // - 1er affichage (userInteracted=false) → mute=1 pour que l'autoplay fonctionne
  //   (politique navigateur stricte sur <iframe>)
  // - Après interaction + muted=false → mute=0 : l'iframe est RECHARGÉE avec le
  //   son activé. C'est la SEULE façon fiable d'activer le son sur un embed
  //   YouTube/Vimeo/TikTok, car `postMessage unMute` est souvent ignoré sans
  //   interaction native dans le contexte de l'iframe.
  const youtubeEmbedUrl = useMemo(() => {
    if (!short.embed_url || !short.youtube_id) return short.embed_url;
    const base = short.embed_url;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const shouldMute = muted || !userInteracted;
    const params = [
      'enablejsapi=1',
      'rel=0',
      'modestbranding=1',
      'playsinline=1',
      origin ? `origin=${encodeURIComponent(origin)}` : null,
    ].filter(Boolean);
    if (active) {
      params.push('autoplay=1');
      params.push(`mute=${shouldMute ? 1 : 0}`);
    }
    const cleanBase = base.replace(/[?&](enablejsapi|rel|modestbranding|playsinline|autoplay|mute|origin)=[^&]*/g, '');
    return `${cleanBase}${cleanBase.includes('?') ? '&' : '?'}${params.join('&')}`;
  }, [short.embed_url, short.youtube_id, active, muted, userInteracted]);

  // URL embed générique (Vimeo / Dailymotion / TikTok) — on reconstruit en
  // injectant les paramètres son adaptés au provider. Vimeo supporte `muted=`,
  // Dailymotion `mute=`, TikTok ne supporte pas (fallback natif).
  const genericEmbedUrl = useMemo(() => {
    if (!short.embed_url || short.youtube_id) return short.embed_url;
    const shouldMute = muted || !userInteracted;
    const base = short.embed_url;
    if (!active) return base;
    const cleanBase = base.replace(/[?&](autoplay|muted|mute)=[^&]*/g, '');
    const sep = cleanBase.includes('?') ? '&' : '?';
    if (short.source_type === 'vimeo') {
      return `${cleanBase}${sep}autoplay=1&muted=${shouldMute ? 1 : 0}&playsinline=1`;
    }
    if (short.source_type === 'dailymotion') {
      return `${cleanBase}${sep}autoplay=1&mute=${shouldMute ? 1 : 0}`;
    }
    // TikTok et autres : on laisse tel quel (pas d'API son)
    return base;
  }, [short.embed_url, short.youtube_id, short.source_type, active, muted, userInteracted]);

  return (
    <div
      className="relative h-full w-full flex items-center justify-center overflow-hidden"
      data-testid={`shorts-slide-${short.id}`}
      data-active={active ? 'true' : 'false'}
    >
      {/* Cadre vidéo : 9/16 vertical, centré, flouté en arrière-plan sur desktop */}
      <div
        className="relative mx-auto bg-black overflow-hidden shadow-2xl md:rounded-2xl"
        style={{ aspectRatio: '9 / 16', maxHeight: '100%', height: '100%', maxWidth: '420px', width: '100%' }}
      >
        {isNative && short.video_url && (
          <video
            ref={registerVideo}
            className="absolute inset-0 w-full h-full object-cover cursor-pointer"
            src={short.video_url}
            poster={short.poster_url || undefined}
            muted={muted}
            playsInline
            preload={active ? 'auto' : 'metadata'}
            onClick={handleTap}
            onTouchEnd={(e) => { e.preventDefault(); handleTap(e); }}
            onEnded={onEnded}
            data-testid={`shorts-video-${short.id}`}
          />
        )}
        {isEmbed && !isNative && (
          <>
            {/* Zone transparente overlay captant les taps PAR-DESSUS l'iframe.
                Les iframes YouTube/TikTok absorbent naturellement les clics, donc
                on crée notre propre couche d'interaction. L'iframe continue de jouer
                en arrière-plan via autoplay=1. Les contrôles natifs YouTube restent
                accessibles via l'URL (fullscreen, etc.) mais pas en tap direct —
                remplacés par notre barre d'actions latérale (pause/play, son, like).
                Note : z-[5] laisse les éléments z >= 10 (header, side-nav, texte, coeurs) au-dessus. */}
            <div
              className="absolute inset-0 z-[5] cursor-pointer"
              onClick={handleTap}
              onTouchEnd={(e) => { e.preventDefault(); handleTap(e); }}
              data-testid={`shorts-tap-overlay-${short.id}`}
              aria-hidden="true"
            />
            {/* Poster YouTube pré-chargé : s'affiche INSTANTANÉMENT en arrière-plan
                pendant le chargement de l'iframe. Dès que l'iframe est prête,
                elle recouvre naturellement le poster (fond noir YouTube). */}
            {short.youtube_id && (
              <img
                src={`https://i.ytimg.com/vi/${short.youtube_id}/maxresdefault.jpg`}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
                loading={active ? 'eager' : 'lazy'}
                onError={(e) => {
                  // Fallback vers hqdefault si maxres indisponible (shorts < 720p ou récents)
                  e.currentTarget.src = `https://i.ytimg.com/vi/${short.youtube_id}/hqdefault.jpg`;
                }}
              />
            )}
            <iframe
              // La `key` inclut l'état `muted` → quand on change de muet à non-muet,
              // React remonte l'iframe → YouTube/Vimeo redémarre AVEC le son (seule
              // méthode fiable de contourner la politique autoplay stricte).
              key={active ? `${short.id}-active-${muted ? 'm' : 'u'}` : short.id}
              ref={registerIframe}
              className="absolute inset-0 w-full h-full"
              src={active ? (short.youtube_id ? youtubeEmbedUrl : genericEmbedUrl) : short.embed_url}
              title={short.title}
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              data-testid={`shorts-iframe-${short.id}`}
            />
            {/* Lien de secours « Ouvrir sur la source » — invisible par défaut,
                reste accessible via Tab/clavier et depuis la carte si l'iframe
                refuse de jouer (restriction d'embed, bloqueur, etc.). */}
            {(() => {
              let href = null;
              let label = null;
              if (short.youtube_id) {
                href = `https://youtube.com/shorts/${short.youtube_id}`;
                label = 'Ouvrir sur YouTube ↗';
              } else if (short.source_type === 'tiktok' && short.source_url_original) {
                href = short.source_url_original;
                label = 'Ouvrir sur TikTok ↗';
              } else if (short.source_type === 'instagram' && short.source_url_original) {
                href = short.source_url_original;
                label = 'Ouvrir sur Instagram ↗';
              } else if (short.source_type === 'vimeo' && short.source_url_original) {
                href = short.source_url_original;
                label = 'Ouvrir sur Vimeo ↗';
              } else if (short.source_type === 'dailymotion' && short.source_url_original) {
                href = short.source_url_original;
                label = 'Ouvrir sur Dailymotion ↗';
              }
              if (!href) return null;
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-28 left-3 z-10 px-2 py-1 rounded-full bg-black/50 backdrop-blur-md text-white/70 text-[9px] font-bold uppercase tracking-wider opacity-50 hover:opacity-100 transition-opacity"
                  data-testid={`shorts-external-fallback-${short.id}`}
                >
                  {label}
                </a>
              );
            })()}
          </>
        )}

        {/* ========= COEURS PULSANTS (double-tap like) =========
            Rendus en absolute aux coordonnées du tap, animation CSS 800ms puis
            auto-nettoyés via setTimeout dans le parent. */}
        {heartPulses?.map((p) => (
          <div
            key={p.id}
            className="absolute z-30 pointer-events-none"
            style={{ left: p.x - 48, top: p.y - 48 }}
            aria-hidden="true"
          >
            <Heart
              className="w-24 h-24 text-red-500 fill-red-500 drop-shadow-[0_4px_16px_rgba(239,68,68,0.5)]"
              style={{ animation: 'heart-pop 850ms ease-out forwards' }}
            />
          </div>
        ))}

        {/* Dégradé bas pour lisibilité du texte — s'estompe aussi avec l'UI pour
            laisser l'image totalement libre pendant la lecture. */}
        <div
          className={`absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none transition-opacity duration-300 ${uiVisible ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* Overlay texte en bas — s'estompe en même temps que les autres UI. */}
        <div
          className={`absolute inset-x-0 bottom-0 p-4 md:p-5 pointer-events-none transition-opacity duration-300 ${uiVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="flex items-center gap-2 flex-wrap pointer-events-auto">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white"
              style={{ background: themeColor }}
            >
              {short.theme_label}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white ${kindColor}`}
            >
              {kindLabel}
            </span>
          </div>
          <h2 className="text-white text-lg md:text-xl font-extrabold leading-tight mt-2" data-testid="shorts-title">
            {short.title}
          </h2>
          {short.description && (
            <p className="text-white/80 text-sm leading-snug mt-1 line-clamp-2">{short.description}</p>
          )}
          {short.author_name && (
            <p className="text-white/60 text-[11px] mt-1.5 italic">{short.author_name}</p>
          )}
          {short.cta_href && short.cta_label && (
            <Link
              to={short.cta_href}
              className="pointer-events-auto inline-flex items-center justify-center mt-3 px-4 py-2 rounded-full bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-white/90"
              data-testid={`shorts-cta-${short.id}`}
            >
              {short.cta_label}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
